import { useEffect, useRef, useState } from 'react';
import { ClapText } from '../motion';
import { RH_MEDIA } from '../../lib/rhapsodyMedia';

/**
 * "The Problem" — a two-panel infographic.
 *
 *   LEFT  (data)      the sequence-coherence chart with the 87% failure
 *                     stat folded in as its conclusion.
 *   RIGHT (spotlight) an auto-cycling image accordion: three shots that
 *                     decay across the sequence, one expanded at a time.
 *
 * Colour carries the argument on one warm spectrum — clean cream (coherent)
 * → ember (drifting) → deep brand red (broken). The chart line, the stat and
 * the spotlight tone-borders all sample the same ramp, so the eye learns the
 * decay once.
 */

/* Sampled sequence coherence, shot 01 → shot 40. Small plateaus and
   rebounds keep the curve reading as data rather than a smooth swoosh. */
const COHERENCE = [100, 92, 86, 87, 78, 72, 74, 64, 57, 52, 45, 36, 30, 19, 10, 4];

/* Plot box in SVG user units. The container is given this exact aspect
   ratio, so the SVG maps 1:1 to it and every %-positioned overlay
   (gridlines, callouts) lands on the right data coordinate. */
const PLOT_W = 432;
const PLOT_H = 250;

/** Catmull-Rom → cubic bézier, so the sampled points join smoothly. */
function smoothPath(points: [number, number][]) {
  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return d;
}

const CHART_POINTS: [number, number][] = COHERENCE.map((v, i) => [
  (i / (COHERENCE.length - 1)) * PLOT_W,
  PLOT_H - (v / 100) * PLOT_H,
]);

const LINE_PATH = smoothPath(CHART_POINTS);
const AREA_PATH = `${LINE_PATH} L ${PLOT_W} ${PLOT_H} L 0 ${PLOT_H} Z`;
const END_POINT = CHART_POINTS[CHART_POINTS.length - 1];

const SHOTS = [
  { slate: 'SHOT 03', tone: 'cool', img: RH_MEDIA.problem.enterprise, issue: 'No shared memory', note: 'Coherent — the world still holds together.' },
  { slate: 'SHOT 27', tone: 'warm', img: RH_MEDIA.problem.filmmakers, issue: 'Style & character drift', note: 'Halfway in, the look and cast start to slip.' },
  { slate: 'SHOT 40', tone: 'hot', img: RH_MEDIA.problem.creators, issue: 'Broken cinematic grammar', note: 'By the end, continuity is gone entirely.' },
];

/* How long each spotlight frame holds before advancing. */
const CYCLE_MS = 4000;

export function RhapsodyProblem() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const statRef = useRef<HTMLSpanElement>(null);

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  /* Auto-cycle the spotlight. Keyed on `active`, so every frame — whether it
     arrives by timer or by tap — gets its own full CYCLE_MS on screen. */
  useEffect(() => {
    if (paused) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    const id = window.setTimeout(() => {
      setActive((prev) => (prev + 1) % SHOTS.length);
    }, CYCLE_MS);
    return () => window.clearTimeout(id);
  }, [active, paused]);

  /* Draw the curve from its true length, then run the counter on scroll-in. */
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const lines = Array.from(section.querySelectorAll<SVGPathElement>('.rp-chart-line'));
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      if (statRef.current) statRef.current.textContent = '87';
      section.classList.add('rp-in');
      return;
    }

    lines.forEach((path) => {
      const len = path.getTotalLength();
      path.style.strokeDasharray = `${len}`;
      path.style.strokeDashoffset = `${len}`;
    });

    let raf = 0;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        section.classList.add('rp-in');
        lines.forEach((path) => { path.style.strokeDashoffset = '0'; });

        const started = performance.now();
        const tick = (now: number) => {
          const t = Math.min(1, (now - started) / 1600);
          const eased = 1 - Math.pow(1 - t, 3);
          if (statRef.current) statRef.current.textContent = `${Math.round(eased * 87)}`;
          if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        io.disconnect();
      },
      { threshold: 0.15 }
    );
    io.observe(section);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className="rh-section rp-section" id="problem" ref={sectionRef}>
      <div className="rp-container">
        {/* ── Statement ── */}
        <div className="rp-statement">
          <p className="rp-body">The AI video continuity engine that keeps characters, story, and shots consistent from script to final cut.</p>

          <ClapText
            text="Today's video models are brilliant one-shot artists yet [[terrible collaborators.]]"
            as="h2"
            className="rp-headline"
          />
          <p className="rp-body">
            Every generation is stateless. The model has no memory of what it
            made thirty seconds ago, and no way to know whether shot 40
            contradicts shot 3 — or whether the sequence breaks the basic
            grammar of filmmaking. You get gorgeous, incoherent output.
          </p>
        </div>

        {/* ── Proof: data panel (left) + spotlight (right) ── */}
        <div className="rp-proof">
          {/* LEFT — coherence chart + 87% stat */}
          <div className="rp-data-card">
            <div className="rp-data-head">
              <div className="rp-data-title">
                <span className="rp-data-kicker">Sequence coherence</span>
                <span className="rp-data-sub">Memory decays with every shot</span>
              </div>
              <span className="rp-mem-chip">
                <span className="rp-mem-dot" aria-hidden="true" />
                Memory <b>OFF</b>
              </span>
            </div>

            <div className="rp-chart-core">
              <div className="rp-y-axis" aria-hidden="true">
                <span>100%</span>
                <span>50%</span>
                <span>0%</span>
              </div>

              <div className="rp-plot" style={{ aspectRatio: `${PLOT_W} / ${PLOT_H}` }}>
                <span className="rp-grid rp-grid-1" aria-hidden="true" />
                <span className="rp-grid rp-grid-2" aria-hidden="true" />
                <span className="rp-grid rp-grid-3" aria-hidden="true" />
                <span className="rp-grid-v" aria-hidden="true" />

                <svg
                  viewBox={`0 0 ${PLOT_W} ${PLOT_H}`}
                  className="rp-chart-svg"
                  role="img"
                  aria-label="Sequence coherence decays from 100% at shot 01 to near zero by shot 40."
                >
                  <defs>
                    <linearGradient id="rpLineGrad" x1="0" y1="0" x2={PLOT_W} y2="0" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#ffcf9f" />
                      <stop offset="24%" stopColor="#ffb27a" />
                      <stop offset="45%" stopColor="#ff945a" />
                      <stop offset="62%" stopColor="#ff7d40" />
                      <stop offset="76%" stopColor="#ff6329" />
                      <stop offset="89%" stopColor="#ff5016" />
                      <stop offset="100%" stopColor="#cc3300" />
                    </linearGradient>

                    <linearGradient id="rpFillGrad" x1="0" y1="0" x2={PLOT_W} y2="0" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#ffb27a" stopOpacity="0.30" />
                      <stop offset="45%" stopColor="#ff945a" stopOpacity="0.24" />
                      <stop offset="76%" stopColor="#ff6329" stopOpacity="0.22" />
                      <stop offset="100%" stopColor="#cc3300" stopOpacity="0.3" />
                    </linearGradient>

                    <linearGradient id="rpFade" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fff" stopOpacity="0.85" />
                      <stop offset="70%" stopColor="#fff" stopOpacity="0.12" />
                      <stop offset="100%" stopColor="#fff" stopOpacity="0" />
                    </linearGradient>
                    <mask id="rpFillMask">
                      <rect x="0" y="0" width={PLOT_W} height={PLOT_H} fill="url(#rpFade)" />
                    </mask>

                    <filter id="rpGlow" x="-20%" y="-40%" width="140%" height="180%">
                      <feGaussianBlur stdDeviation="5" />
                    </filter>
                  </defs>

                  <path d={AREA_PATH} fill="url(#rpFillGrad)" mask="url(#rpFillMask)" className="rp-chart-fill" />

                  <path
                    d={LINE_PATH}
                    fill="none"
                    stroke="url(#rpLineGrad)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    filter="url(#rpGlow)"
                    className="rp-chart-line rp-chart-line-glow"
                  />
                  <path
                    d={LINE_PATH}
                    fill="none"
                    stroke="url(#rpLineGrad)"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="rp-chart-line"
                  />

                  <g className="rp-dot rp-dot-start">
                    <circle cx="0" cy="0" r="7.5" fill="#ffb27a" opacity="0.2" />
                    <circle cx="0" cy="0" r="4.6" fill="#0e0e12" stroke="#ffc79a" strokeWidth="2" />
                  </g>
                  <g className="rp-dot rp-dot-end">
                    <circle cx={END_POINT[0]} cy={END_POINT[1]} r="9" fill="#ff5210" opacity="0.16" className="rp-dot-halo" />
                    <circle cx={END_POINT[0]} cy={END_POINT[1]} r="4.6" fill="#0e0e12" stroke="#ff6a24" strokeWidth="2" />
                  </g>
                </svg>

                <div className="rp-callout rp-callout-end">
                  Coherence lost
                </div>
              </div>
            </div>

            <div className="rp-chart-footer" aria-hidden="true">
              <span className="rp-footer-label">Shot 03</span>
              <span className="rp-footer-label">Shot 27</span>
              <span className="rp-footer-label">Shot 40</span>
            </div>

            {/* 87% conclusion, anchored to the chart */}
            <div className="rp-data-stat">
              <span className="rp-stat-num">
                <span ref={statRef}>0</span>%<i>+</i>
              </span>
              <span className="rp-stat-cap">
                Continuity failure<br />by shot 40
              </span>
            </div>
          </div>

          {/* RIGHT — auto-cycling spotlight accordion */}
          <div
            className="rp-spotlight"
            data-paused={paused ? 'true' : 'false'}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            {SHOTS.map((s, idx) => (
              <button
                type="button"
                key={s.slate}
                className="rp-spot-card"
                data-tone={s.tone}
                data-active={idx === active ? 'true' : 'false'}
                onClick={() => setActive(idx)}
                onFocus={() => setPaused(true)}
                onBlur={() => setPaused(false)}
                aria-label={`${s.slate}: ${s.issue}`}
                aria-pressed={idx === active}
              >
                <img
                  src={s.img}
                  alt={s.slate}
                  loading="lazy"
                  decoding="async"
                  className="rp-spot-img"
                />
                <span className="rp-spot-scrim" aria-hidden="true" />

                <span className="rp-spot-slate">{s.slate}</span>

                <span className="rp-spot-body">
                  <span className="rp-spot-issue">{s.issue}</span>
                  <span className="rp-spot-note">{s.note}</span>
                </span>

                {/* time-to-next progress; remounts each frame via key */}
                {idx === active && <span className="rp-spot-progress" key={active} aria-hidden="true" />}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
